import React, { useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { Upload } from 'lucide-react';
import ChatbotContext from '../../context/ChatbotContext/ChatbotContext';
import CustomModal from '../CustomModal/CustomModal';
import CustomInput from '../CustomInput/CustomInput';
import CustomTextArea from '../CustomTextArea/CustomTextArea';
import DatePickerInput from '../DatePickerInput/DatePickerInput';
import Button from '../Button/Button';
import NotificationSnackbar from '../NotificationSnackbar/NotificationSnackbar';
import KnowledgeBaseService from '../../services/KnowledgeBaseService/KnowledgeBaseService';

interface FormValues {
  name: string;
  description: string | null;
}

interface UploadKnowledgeBaseItemModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preSelectedFile?: File;
}

function UploadKnowledgeBaseItemModal({
  isOpen,
  onClose,
  onSuccess,
  preSelectedFile
}: Readonly<UploadKnowledgeBaseItemModalProps>): React.ReactElement {
  const [file, setFile] = useState<File | null>(null);
  const [nextReviewDate, setNextReviewDate] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);

  const { currentChatbot } = useContext(ChatbotContext);

  const validationSchema = Yup.object().shape({
    name: Yup.string().required(
      'Name is required for your knowledge base item'
    ),
    description: Yup.string().nullable()
  });

  const {
    handleSubmit,
    reset,
    control,
    formState: { errors }
  } = useForm<FormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: { name: '', description: '' }
  });

  useEffect(() => {
    if (preSelectedFile && isOpen) {
      setFile(preSelectedFile);
    }
  }, [preSelectedFile, isOpen]);

  const handleFileChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const selectedFile = event.target.files?.[0] ?? null;
    const inputElement = event.target;

    if (selectedFile) {
      const allowedExtensions = ['.txt', '.md', '.pdf', '.docx'];
      const allowedMimeTypes = [
        'text/plain',
        'text/markdown',
        'application/pdf',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      ];

      const fileName = selectedFile.name.toLowerCase();
      const hasValidExtension = allowedExtensions.some((ext) =>
        fileName.endsWith(ext)
      );
      const hasValidMimeType = allowedMimeTypes.includes(selectedFile.type);

      if (!hasValidExtension || !hasValidMimeType) {
        setErrorMessage('Only TXT, MD, PDF, and DOCX files are allowed.');
        setShowError(true);
        inputElement.value = '';
        return;
      }
    }

    setFile(selectedFile);
  };

  const handleClose = (): void => {
    setFile(null);
    setNextReviewDate(null);
    reset();
    setErrorMessage('');
    onClose();
  };

  const onSubmit = (data: FormValues): void => {
    setIsLoading(true);
    setErrorMessage('');
    if (!file || !currentChatbot) {
      setErrorMessage('Please upload an item to your knowledge base');
      setShowError(true);
      setIsLoading(false);
    } else {
      KnowledgeBaseService.uploadKnowledgeBaseItem(
        currentChatbot.id,
        data.name,
        data.description,
        nextReviewDate,
        file
      )
        .then(() => {
          setFile(null);
          setNextReviewDate(null);
          reset();
          setSuccessMessage('Item uploaded successfully');
          setShowSuccess(true);
          onSuccess();
          handleClose();
        })
        .catch((err) => {
          if (err.name === 'CanceledError') return;
          const error =
            err.apiError?.message ||
            'Something went wrong. Please try again later.';
          setErrorMessage(error);
          setShowError(true);
        })
        .finally(() => setIsLoading(false));
    }
  };

  return (
    <>
      <CustomModal
        open={isOpen}
        onClose={handleClose}
        title='Add document'
        maxWidth='md'
      >
        <form
          onSubmit={handleSubmit(onSubmit)}
          noValidate
          className='space-y-4 min-w-0'
        >
          <Controller
            name='name'
            control={control}
            render={({ field }) => (
              <CustomInput
                label='Item name'
                type='name'
                {...field}
                error={errors.name}
              />
            )}
          />
          <Controller
            name='description'
            control={control}
            render={({ field }) => (
              <CustomTextArea
                label='Description'
                type='description'
                {...field}
                value={field.value ?? ''}
                error={errors.description}
              />
            )}
          />
          <DatePickerInput
            label='Next review date'
            date={nextReviewDate ?? undefined}
            setDate={(date) => setNextReviewDate(date ?? null)}
          />
          <div className='space-y-2'>
            <div className='flex gap-2 items-center'>
              <div className='flex-1 min-w-0'>
                {file ? (
                  <div className='flex items-center gap-2 p-2 bg-gray-50 border border-gray-300 rounded text-sm min-w-0'>
                    <span className='truncate text-gray-700 flex-1 min-w-0'>
                      {file.name}
                    </span>
                    <button
                      type='button'
                      onClick={() => setFile(null)}
                      className='text-gray-500 hover:text-gray-700 flex-shrink-0'
                    >
                      ✕
                    </button>
                  </div>
                ) : (
                  <CustomInput
                    id='file-upload'
                    type='file'
                    label=''
                    accept='.txt,.md,.pdf,.docx'
                    onChange={handleFileChange}
                    className='h-9'
                    fullWidth
                    containerClassName='mb-0'
                  />
                )}
              </div>
              <Button
                type='submit'
                loading={isLoading}
                icon={<Upload size='16' />}
                className='h-9 flex-shrink-0'
              >
                Upload
              </Button>
            </div>
            <p className='text-xs text-gray-500'>
              Supported formats: TXT, MD, PDF, DOCX
            </p>
          </div>
        </form>
      </CustomModal>

      <NotificationSnackbar
        message={successMessage}
        severity='success'
        open={showSuccess}
        onClose={() => setShowSuccess(false)}
      />
      <NotificationSnackbar
        message={errorMessage}
        severity='error'
        open={showError}
        onClose={() => setShowError(false)}
      />
    </>
  );
}

export default UploadKnowledgeBaseItemModal;
